import React, { useMemo, useState } from 'react';
import { formatDate } from 'date-fns';
import { ru } from 'date-fns/locale';
import { Box } from '@cp/ds/src/components/box';
import { Button } from '@cp/ds/src/components/button';
import { Link } from '@cp/ds/src/components/link';
import { Stack } from '@cp/ds/src/components/stack';
import { Typography } from '@cp/ds/src/components/typography';
import { Skeleton } from '@cp/ds/src/components/skeleton';
import { useTheme } from '@cp/ds/src/hooks/useTheme';
import { ApiServerEntityIDType } from '@cp/utils/apiServer/types';
import { adminPersonalQualitiesApi } from 'src/entities/personalQualities';
import { usePersonalQualitiesModals } from '../model/usePersonalQualitiesModals';

const STATUS_GREEN = '#22C34B';
const RESULT_BG = '#F5FAFC';
const RESULT_TEXT = '#536477';

export interface PersonalQualitiesSectionProps {
  clientId: ApiServerEntityIDType;
}

export const PersonalQualitiesSection: React.FC<PersonalQualitiesSectionProps> = ({ clientId }) => {
  const theme = useTheme();
  const { openAnswersModal, openPromptEditModal, openMultiAnalysisModal } = usePersonalQualitiesModals();
  const { data, isLoading } = adminPersonalQualitiesApi.endpoints.getPersonalQualities.useQuery({ clientId });
  const [analyzePersonalQualities, { isLoading: isAnalyzing }] =
    adminPersonalQualitiesApi.endpoints.analyzePersonalQualities.useMutation();
  const [deletePersonalQualitiesAnalysis, { isLoading: isDeletingAnalysis }] =
    adminPersonalQualitiesApi.endpoints.deletePersonalQualitiesAnalysis.useMutation();
  const [analyzeError, setAnalyzeError] = useState<string | undefined>();
  const [deleteError, setDeleteError] = useState<string | undefined>();

  const statusText = useMemo(() => {
    if (!data?.id) {
      return 'Тест не начат';
    }
    if (data.isCompleted) {
      return 'Тест завершен';
    }
    if (!data.answeredCount) {
      return 'Начат';
    }
    return `${data.answeredCount} из ${data.totalQuestions} вопросов`;
  }, [data]);

  const aiAnalysisDateText = useMemo(() => {
    if (!data?.aiAnalysis?.analyzedAt) {
      return null;
    }
    return formatDate(new Date(data.aiAnalysis.analyzedAt), 'dd MMMM yyyy, HH:mm', { locale: ru });
  }, [data?.aiAnalysis?.analyzedAt]);

  const canViewAnswers = Boolean(data?.answers && Object.keys(data.answers).length > 0);
  const isCompleted = Boolean(data?.isCompleted);
  const aiAnalysis = data?.aiAnalysis;
  const hasAiAnalysis = Boolean(aiAnalysis);

  const handleViewAnswers = () => {
    if (!data?.answers) {
      return;
    }
    void openAnswersModal({ answers: data.answers });
  };

  const handleViewPrompt = () => {
    void openPromptEditModal({});
  };

  const handleAnalyze = async () => {
    setAnalyzeError(undefined);
    try {
      await analyzePersonalQualities({ clientId }).unwrap();
    } catch (error) {
      let message = 'Не удалось выполнить анализ ИИ';
      if (error && typeof error === 'object' && 'data' in error) {
        const errorData = (error as { data?: { message?: string | string[] } }).data;
        if (typeof errorData?.message === 'string') {
          message = errorData.message;
        } else if (Array.isArray(errorData?.message) && errorData.message[0]) {
          message = errorData.message[0];
        }
      }
      setAnalyzeError(message);
    }
  };

  const handleMultiAnalyze = () => {
    void openMultiAnalysisModal({ clientId });
  };

  const handleDeleteAnalysis = async () => {
    setDeleteError(undefined);
    try {
      await deletePersonalQualitiesAnalysis({ clientId }).unwrap();
    } catch (error) {
      let message = 'Не удалось удалить анализ ИИ';
      if (error && typeof error === 'object' && 'data' in error) {
        const errorData = (error as { data?: { message?: string | string[] } }).data;
        if (typeof errorData?.message === 'string') {
          message = errorData.message;
        } else if (Array.isArray(errorData?.message) && errorData.message[0]) {
          message = errorData.message[0];
        }
      }
      setDeleteError(message);
    }
  };

  return (
    <Box
      sx={{
        backgroundColor: 'background.default',
        borderRadius: '20px',
        border: `1px solid ${theme.palette['gray light']}`,
        p: 2,
      }}
    >
      <Stack gap={1.5}>
        <Typography sx={{ typography: 'button bold' }}>{'Оценка личных качеств'}</Typography>

        {isLoading ? (
          <Skeleton height={24} width={240} />
        ) : (
          <Stack alignItems="center" direction="row" flexWrap="wrap" gap={1.5}>
            <Stack alignItems="center" direction="row" gap={0.5}>
              <Typography sx={{ color: 'brand gray' }}>{'Статус:'}</Typography>
              <Typography sx={{ color: isCompleted ? STATUS_GREEN : 'text.primary' }}>{statusText}</Typography>
            </Stack>
            {canViewAnswers ? (
              <Link onClick={handleViewAnswers} role="button" size="small" underline>
                {'Посмотреть ответы'}
              </Link>
            ) : null}
          </Stack>
        )}

        {isCompleted && !hasAiAnalysis ? (
          <Stack alignItems="flex-start" gap={1}>
            <Stack alignItems="center" direction="row" flexWrap="wrap" gap={1.5}>
              <Button
                color="white"
                disabled={isAnalyzing}
                onClick={() => void handleAnalyze()}
                size="small"
                sx={{ border: `1px solid ${theme.palette['brand pink']}` }}
                variant="primary"
              >
                {'Анализ ИИ'}
              </Button>
              <Button
                color="white"
                disabled={isAnalyzing}
                onClick={handleMultiAnalyze}
                size="small"
                sx={{ border: `1px solid ${theme.palette['brand pink']}` }}
                variant="primary"
              >
                {'Мультианализ'}
              </Button>
              <Link onClick={handleViewPrompt} role="button" size="small" underline>
                {'Редактировать промпт'}
              </Link>
            </Stack>
            {analyzeError ? (
              <Typography sx={{ color: 'error.main' }} variant="caption">
                {analyzeError}
              </Typography>
            ) : null}
          </Stack>
        ) : null}

        {aiAnalysis ? (
          <Stack gap={0.5}>
            <Box
              sx={{
                backgroundColor: RESULT_BG,
                borderRadius: '12px',
                p: 1.5,
              }}
            >
              <Typography sx={{ color: RESULT_TEXT, whiteSpace: 'pre-wrap' }}>
                {`Оценка: ${aiAnalysis.score}/100\nСтатус: ${aiAnalysis.statusLabel}\nВывод:\n${aiAnalysis.summary}`}
              </Typography>
            </Box>
            <Stack alignItems="center" direction="row" flexWrap="wrap" gap={1.5}>
              <Link onClick={handleViewPrompt} role="button" size="small" underline>
                {'Посмотреть промт'}
              </Link>
              <Link
                onClick={() => {
                  if (isDeletingAnalysis) {
                    return;
                  }
                  void handleDeleteAnalysis();
                }}
                role="button"
                size="small"
                sx={isDeletingAnalysis ? { opacity: 0.5, pointerEvents: 'none' } : undefined}
                underline
              >
                {'Удалить анализ'}
              </Link>
              {aiAnalysisDateText ? (
                <Typography sx={{ color: 'brand gray', ml: 'auto', typography: 'caption bold' }}>
                  {`Дата анализа: ${aiAnalysisDateText}`}
                </Typography>
              ) : null}
            </Stack>
            {deleteError ? (
              <Typography sx={{ color: 'error.main' }} variant="caption">
                {deleteError}
              </Typography>
            ) : null}
          </Stack>
        ) : null}
      </Stack>
    </Box>
  );
};
